import { useState, useEffect, useMemo } from "react";
import { Loader2 } from "lucide-react";
import { OptionCard } from "./OptionCard";

type OptionItem = {
  title: string;
  description: string;
  image: string;
};

type OptionGroup = {
  label: string;
  items: OptionItem[];
};

type OptionsCategory = {
  id: string;
  label: string;
  items?: OptionItem[];
  groups?: OptionGroup[];
};

type OptionsLibraryPayload = {
  categories: OptionsCategory[];
};

type OptionsCategorySectionProps = {
  /** `categories[].id` in `public/options-library.json` (e.g. `boxTypes`). */
  categoryId: string;
  heading?: string;
  subheading?: string;
  id?: string;
};

export function OptionsCategorySection({
  categoryId,
  heading,
  subheading,
  id: sectionId,
}: OptionsCategorySectionProps) {
  const [library, setLibrary] = useState<OptionsLibraryPayload | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch("/options-library.json")
      .then((res) => {
        if (!res.ok) throw new Error("Could not load options");
        return res.json() as Promise<OptionsLibraryPayload>;
      })
      .then((data) => {
        if (!cancelled) setLibrary(data);
      })
      .catch(() => {
        if (!cancelled) setLoadError("Couldn’t load options.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const category = useMemo(() => {
    if (!library?.categories) return null;
    return library.categories.find((c) => c.id === categoryId) ?? null;
  }, [library, categoryId]);

  const title = heading ?? category?.label ?? "Options";
  const desc = subheading;

  return (
    <section id={sectionId} className="py-24 bg-background">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-5xl font-bold mb-4">{title}</h2>
          {desc && <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{desc}</p>}
          {!desc && category && (
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              From our options library — closures, trays, lids, and features for folding cartons.
            </p>
          )}
        </div>

        {loadError && (
          <p className="text-center text-destructive py-8">{loadError}</p>
        )}

        {!loadError && !library && (
          <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" aria-hidden />
            <span>Loading box types…</span>
          </div>
        )}

        {!loadError && library && !category && (
          <p className="text-center text-muted-foreground py-8">Category not found in options library.</p>
        )}

        {category?.groups?.length ? (
          <div className="space-y-16 md:space-y-20">
            {category.groups.map((group, gi) => (
              <div key={`${category.id}-group-${gi}`}>
                <header className="mb-6 border-b border-border pb-4">
                  <h3 className="text-2xl font-bold">{group.label}</h3>
                </header>
                <div className={category.id === 'paperboard' ? "grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6" : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"}>
                  {group.items.map((option, index) => (
                    <OptionCard key={`${group.label}-${option.title}-${index}`} option={option} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {category && !category.groups?.length && category.items?.length ? (
          <div className={category.id === 'paperboard' ? "grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6" : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"}>
            {category.items.map((option, index) => (
              <OptionCard key={`${option.title}-${index}`} option={option} />
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}
